import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { AccountType } from 'dashboard/models/account-type';
import { Environment } from 'dashboard/models/environment';
import { Graph, GraphNodeConfigAttributes } from 'dashboard/models/graph';
import { GraphDatumType, GraphDatumTypeAttributes } from 'dashboard/models/graph-datum-type';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { loadAccountTypes } from 'dashboard/redux/thunk-actions/account-types';
import {
  loadGraphsAndGraph,
  updateGraph,
  updateGraphNodeConfig,
} from 'dashboard/redux/thunk-actions/graph';
import {
  loadGraphDatumType,
  updateGraphDatumType,
} from 'dashboard/redux/thunk-actions/graph-datum-type';
import DashboardGraphNodeConfigView from 'dashboard/views/DashboardGraphNodeConfigView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

interface DashboardGraphNodeConfigRouteStateProps {
  accountTypes: AccountType[];
  currentEnvironment: Environment;
  graph?: Graph;
  graphDatumType?: GraphDatumType;
}

type Params = { graphDatumTypeId: string };

const DashboardGraphNodeConfigRoute = (
  props: DashboardGraphNodeConfigRouteStateProps & DashboardGraphNodeConfigRouteConnectedProps,
) => {
  const { currentEnvironment, graphDatumType } = props;
  const { graphDatumTypeId } = useParams<Params>() as Params;

  const { loading: loadingGraph } = useApiLoader({
    loader: () => props.loadGraphsAndGraph(currentEnvironment.id),
    deps: [currentEnvironment],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to load graph configuration, please reload to try again',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please reload to try again.',
        MessageType.Failure,
      );
    },
  });

  const { loading: loadingAccountTypes } = useApiLoader({
    loader: () => props.loadAccountTypes(),
    deps: [currentEnvironment],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to load account types, please reload to try again',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please reload to try again.',
        MessageType.Failure,
      );
    },
  });

  const { loading: loadingGraphDatumType } = useApiLoader({
    loader: () => props.loadGraphDatumType(graphDatumTypeId),
    deps: [currentEnvironment, graphDatumTypeId],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to load datum type, please reload to try again',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please reload to try again.',
        MessageType.Failure,
      );
    },
  });

  const {
    loading: isSubmittingUpdateNodeForm,
    error: handleUpdateGraphDatumTypeError,
    callback: handleUpdateGraphDatumType,
  } = useApiCallback(
    async (graphDatumTypeId: string, values: GraphDatumTypeAttributes) => {
      await props.updateGraphDatumType(graphDatumTypeId, values);
      props.showMessage('Successfully edited node type.', MessageType.Success);
    },
    [graphDatumType],
  );

  useApiErrorHandler({
    error: handleUpdateGraphDatumTypeError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to update node type, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    loading: isSubmitting,
    error: handleUpdateGraphError,
    callback: handleUpdateGraphNodeConfig,
  } = useApiCallback(
    async (values: GraphNodeConfigAttributes) => {
      if (props.graph) {
        await props.updateGraphNodeConfig(props.graph.id, values);
        props.showMessage('Successfully updated Graph configuration.', MessageType.Success);
      }
    },
    [props.graph],
  );

  useApiErrorHandler({
    error: handleUpdateGraphError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to update Graph configuration, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  return (
    <DashboardGraphNodeConfigView
      graph={props.graph}
      graphDatumType={props.graphDatumType}
      accountTypes={props.accountTypes}
      isLoading={
        loadingGraph || loadingAccountTypes || loadingGraphDatumType || isSubmittingUpdateNodeForm
      }
      isSubmitting={isSubmitting}
      handleUpdateGraphNodeConfig={handleUpdateGraphNodeConfig}
      handleUpdateGraphDatumType={handleUpdateGraphDatumType}
    />
  );
};

const mapStateToProps = (state: RootState): DashboardGraphNodeConfigRouteStateProps => {
  return {
    accountTypes: state.accountTypes.accountTypes,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    graph: state.graph.graph,
    graphDatumType: state.graphDatumType.graphDatumType,
  };
};

const mapDispatchToProps = {
  loadAccountTypes,
  loadGraphsAndGraph,
  loadGraphDatumType,
  updateGraphDatumType,
  updateGraph,
  updateGraphNodeConfig,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type DashboardGraphNodeConfigRouteConnectedProps = ConnectedProps<typeof connector>;
export default connector(DashboardGraphNodeConfigRoute);
